import { Component } from 'react';

import { uebersetze } from '../../i18n/sprache.js';
import { Button } from '../ui/Button.jsx';

/**
 * Die Fehlergrenze.
 *
 * Ohne sie reisst ein einziger Darstellungsfehler die GANZE Anwendung weg:
 * React hebt einen Fehler beim Rendern bis zur Wurzel, findet dort niemanden,
 * der ihn auffängt, und hängt den Baum aus. Übrig bleibt eine weisse Seite -
 * keine Navigation, keine Meldung, kein Weg zurück ausser F5. Für eine
 * Anwendung, in der jemand gerade eine Rechnung erfasst hat, ist das der
 * schlechteste aller Ausgänge: es sieht aus, als sei alles verloren.
 *
 * Deshalb steht sie an zwei Stellen (siehe AppShell und index.jsx). Die
 * innere fängt den Seiteninhalt ab, damit die Navigation stehen bleibt und
 * man einfach woandershin klicken kann; die äussere fängt alles Übrige.
 *
 * Der Text kommt aus `uebersetze()` statt aus `useTranslation()`. Nicht aus
 * Bequemlichkeit: eine Klassenkomponente hat keine Haken, und vor allem darf
 * die Auffanganzeige nicht selbst von einem Kontext abhängen, der womöglich
 * genau der ist, der gerade zerbrochen ist.
 */
export class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { fehler: null, resetKey: props.resetKey };
  }

  static getDerivedStateFromError(fehler) {
    return { fehler };
  }

  /* Ein Wechsel des Schlüssels - in der Praxis der Pfad - räumt den Fehler
     weg. Ohne das bliebe die Tafel stehen, auch nachdem man in der Navigation
     auf eine ganz andere Seite geklickt hat: React baut den Teilbaum nicht neu
     auf, solange der Zustand einen Fehler trägt. */
  static getDerivedStateFromProps(props, state) {
    if (props.resetKey !== state.resetKey) {
      return { fehler: null, resetKey: props.resetKey };
    }
    return null;
  }

  componentDidCatch(fehler, info) {
    // Die Konsole ist hier das Protokoll. Der Stapel steht in `info`.
    // eslint-disable-next-line no-console
    console.error('Darstellungsfehler aufgefangen', fehler, info?.componentStack);
    this.props.onError?.(fehler, info);
  }

  render() {
    const { fehler } = this.state;
    if (!fehler) {
      return this.props.children;
    }

    return (
      <div className="card" role="alert">
        <div className="empty-state">
          <strong>{uebersetze('fehler.grenze.titel')}</strong>
          <p className="muted" style={{ maxWidth: '56ch' }}>
            {uebersetze('fehler.grenze.text')}
          </p>
          <div className="page-actions" style={{ justifyContent: 'center' }}>
            <Button variant="primary" onClick={() => window.location.reload()}>
              {uebersetze('fehler.grenze.neuLaden')}
            </Button>
            {this.props.onHome ? (
              <Button onClick={this.props.onHome}>{uebersetze('fehler.grenze.zurueck')}</Button>
            ) : null}
          </div>
          {/* Die technische Meldung bleibt erreichbar, aber zugeklappt: sie
              hilft beim Melden und erschreckt niemanden, der sie nicht sucht. */}
          <details style={{ marginTop: 'var(--space-4)', textAlign: 'left', maxWidth: '56ch' }}>
            <summary>{uebersetze('fehler.grenze.details')}</summary>
            <pre className="mono" style={{ whiteSpace: 'pre-wrap', overflowX: 'auto' }}>
              {String(fehler?.message || fehler)}
            </pre>
          </details>
        </div>
      </div>
    );
  }
}
